import { t, getLocale } from '/src/i18n.js';

   class CoffeeRanking extends HTMLElement {
 
    // ── A. Shadow DOM setup ──────────────────────────────────────────────
    constructor() {
      super();
      this._shadow  = this.attachShadow({ mode: "open" });
      this._members = [];
      this._date    = null;
    }
 
    // ── B. Lifecycle ─────────────────────────────────────────────────────
    connectedCallback() {
      this._render();
    }
 
    // ── C. JS property setter
    // Accepts { date: "YYYY-MM-DD", members: [{ id, name, surname, alias, avatar, drank, offered }] }
    set data(obj) {
      this._date    = obj?.date    || null;
      this._members = Array.isArray(obj?.members) ? obj.members : [];
      this._render();
    }
 
    // ── D. Attribute → property bridge ───────────────────────────────────
    static get observedAttributes() { return ["data"]; }
    attributeChangedCallback(name, _, newVal) {
      if (name === "data") {
        try { this.data = JSON.parse(newVal); } catch {}
      }
    }
 
    // ── E. Utilities ─────────────────────────────────────────────────────
 
    _initials(u) {
      return (u.name[0] + u.surname[0]).toUpperCase();
    }
 
    _fmtDate(d) {
      if (!d) return t("today");
      const locale = getLocale();
      return new Date(d + "T00:00:00").toLocaleDateString(locale, {
        weekday: "long", day: "numeric", month: "long"
      });
    }
 
    // ── F. Ranked + enriched member array ────────────────────────────────
    _getRanked() {
      return this._members
        .map(u => ({ ...u, total: (u.drank || 0) + (u.offered || 0) }))
        .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));
    }
 
    // ── G. SVG trophy cup ────────────────────────────────────────────────
    _cupSVG(size = 18) {
      return `<svg width="${size}" height="${size}" viewBox="0 0 20 20" fill="#5D4037"
        xmlns="http://www.w3.org/2000/svg">
        <path d="M6 2h8v8a4 4 0 01-8 0V2z"
              fill="#5D4037" stroke="#F5EDEB" stroke-width="1.2" stroke-linejoin="round"/>
        <path d="M14 4h2a2 2 0 010 4h-2" stroke="#F5EDEB" stroke-width="1.2" stroke-linecap="round"/>
        <path d="M6 4H4a2 2 0 000 4h2"   stroke="#F5EDEB" stroke-width="1.2" stroke-linecap="round"/>
        <path d="M8 14v2M12 14v2"         stroke="#F5EDEB" stroke-width="1.2" stroke-linecap="round"/>
        <path d="M6 18h8"                 stroke="#F5EDEB" stroke-width="1.2" stroke-linecap="round"/>
      </svg>`;
    }
 
    // ── H. Medal icons for top 3 ─────────────────────────────────────────
    _medalSVG(rank) {
      const configs = {
        1: { fill: "#EF9F27", stroke: "#854F0B", text: "#633806" },
        2: { fill: "#B4B2A9", stroke: "#5F5E5A", text: "#2C2C2A" },
        3: { fill: "#F0997B", stroke: "#993C1D", text: "#4A1B0C" },
      };
      const c = configs[rank];
      if (!c) return `<span class="rank-num">${rank}</span>`;
      return `<svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="10" r="8" fill="${c.fill}" stroke="${c.stroke}" stroke-width="1.2"/>
        <text x="10" y="14.5" text-anchor="middle" font-size="10" font-weight="700"
              fill="${c.text}" font-family="system-ui">${rank}</text>
      </svg>`;
    }
 
    // ── I. Build HTML for a single ranking row ────────────────────────────
    _rowHTML(u, rank, isWinner) {
      const initials = this._initials(u);
 
      const avatarHTML = u.avatar
        ? `<img class="avatar" src="${u.avatar}" alt="${u.name}"
               onerror="this.style.display='none';this.nextElementSibling.style.display='flex'">
           <div class="avatar-placeholder" style="display:none">${initials}</div>`
        : `<div class="avatar-placeholder">${initials}</div>`;
 
      const cupHTML   = isWinner
        ? `<span class="cup" title="${t("todays-champion")}">${this._cupSVG(16)}</span>`
        : "";
 
      const medalHTML = rank <= 3 ? this._medalSVG(rank) : rank;
 
      return `
        <div class="row${isWinner ? " winner" : ""}" data-id="${u.id}"
             role="row" tabindex="0"
             aria-label="${u.name} ${u.surname}, rank ${rank}, ${u.total} coffees">
          <div class="rank-cell">${medalHTML}</div>
          <div class="avatar-col">${avatarHTML}</div>
          <div class="info">
            <div class="name">${u.name} ${u.surname} ${cupHTML}</div>
            <div class="alias">${u.alias}</div>
          </div>
          <div class="stats">
            <div class="total">${u.total} ☕</div>
            <div class="breakdown">${u.drank || 0}↓ · ${u.offered || 0}↑</div>
          </div>
        </div>`;
    }
 
    // ── J. Build HTML for a mini bar chart ───────────────────────────────
    _barHTML(ranked, maxTotal) {
      const active = ranked.filter(u => u.total > 0);
      if (!active.length) return "";
 
      const bars = active.map(u => {
        const pct = maxTotal > 0 ? Math.round((u.total / maxTotal) * 100) : 0;
        return `
          <div class="bar-row">
            <div class="bar-name">${u.name}</div>
            <div class="bar-track">
              <div class="bar-fill" style="width:${pct}%"></div>
            </div>
            <div class="bar-val">${u.total}</div>
          </div>`;
      }).join("");
 
      return `<div class="bar-section">${bars}</div>`;
    }
 
    // ── K. Fire custom event on row click ─────────────────────────────────
    _dispatch(id) {
      const member = this._members.find(m => m.id === id);
      if (member) {
        this.dispatchEvent(new CustomEvent("ranking-select", {
          detail: member, bubbles: true, composed: true
        }));
      }
    }
 
    // ── L. Bind row click + keyboard events ───────────────────────────────
    _bindRowEvents() {
      this._shadow.querySelectorAll(".row").forEach(el => {
        el.addEventListener("click", () => this._dispatch(Number(el.dataset.id)));
        el.addEventListener("keydown", e => {
          if (e.key === "Enter" || e.key === " ") this._dispatch(Number(el.dataset.id));
        });
      });
    }
 
    // ── M. Full render ────────────────────────────────────────────────────
    _render() {
      const ranked      = this._getRanked();
      const maxTotal    = ranked[0]?.total || 1;
      const winner      = ranked.find(u => u.total > 0);
      const totalCups   = ranked.reduce((s, u) => s + u.total, 0);
      const activeCount = ranked.filter(u => u.total > 0).length;
 
      const rowsHTML = ranked.length
        ? ranked.map((u, i) =>
            this._rowHTML(u, i + 1, i === 0 && u.total > 0)
          ).join("")
        : `<div class="empty">${t("no-data-today")}</div>`;
 
      const barsHTML = this._barHTML(ranked, maxTotal);
 
      const footerChamp = winner
        ? `<span class="footer-stat">${t("champion-label")} <strong>${winner.name}</strong></span>`
        : "";
 
      this._shadow.innerHTML = `
        <style>
          *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
          :host { display: block; font-family: system-ui, sans-serif; }
 
          .card {
            background: #fff;
            border: 0.5px solid #e0dfd7;
            border-radius: 12px;
            /*overflow: hidden;*/
            /* max-width: 700px; */
          }
 
          .header {
            padding: 14px 16px 12px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            border-bottom: 0.5px solid #e0dfd7;
            flex-wrap: wrap;
            gap: 6px;
          }
 
          .title {
            font-size: 15px;
            font-weight: 500;
            color: #1a1a1a;
            display: flex;
            align-items: center;
            gap: 8px;
          }
 
          .title-icon {
            width: 28px; height: 28px;
            background: #5D4037;
            border-radius: 8px;
            display: flex; align-items: center; justify-content: center;
            flex-shrink: 0;
          }
 
          .date-label {
            font-size: 12px;
            color: #aaa;
          }
 
          /* ── Rows ───── */
          .list { display: flex; flex-direction: column; }
 
          .row {
            display: grid;
            grid-template-columns: 32px 44px 1fr auto;
            gap: 0 12px;
            align-items: center;
            padding: 11px 16px;
            border-bottom: 0.5px solid #e0dfd7;
            cursor: pointer;
            transition: background 0.1s;
          }
 
          .row:last-child { border-bottom: none; }
          .row:hover { background: #f7f6f2; }
          .row:focus-visible { outline: 2px solid #4a90d9; outline-offset: -2px; }
 
          /*
          .row.winner { background: #FAEEDA; }
          .row.winner:hover { background: #FAC775; }
          */
          .row.winner:hover { background: #FAEEDA; }
 
          /* ── Rank cell ─── */
          .rank-cell {
            display: flex;
            align-items: center;
            justify-content: center;
          }
 
          .rank-num {
            font-size: 12px;
            font-weight: 500;
            color: #aaa;
          }
 
          /* ── Avatar ─── */
          .avatar-col { display: flex; align-items: center; }
 
          .avatar {
            width: 40px; height: 40px;
            border-radius: 50%;
            object-fit: cover;
            border: 0.5px solid #A28077;
            background: #A28077;
            display: block;
          }
 
          .avatar-placeholder {
            width: 40px; height: 40px;
            border-radius: 50%;
            background: #A28077;
            color: #F5EDEB;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 13px;
            font-weight: 500;
            flex-shrink: 0;
          }
 
          .winner .avatar-placeholder {
            background: #EF9F27;
            color: #412402;
          }
 
          /* ── Info ─── */
          .info { min-width: 0; }
 
          .name {
            font-size: 14px;
            font-weight: 500;
            color: #1a1a1a;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            display: flex;
            align-items: center;
            gap: 5px;
          }
 
          .winner .name { color: #633806; }
 
          .alias {
            font-size: 11px;
            color: #aaa;
            margin-top: 1px;
          }
 
          .winner .alias { color: #854F0B; }
 
          .cup {
            display: inline-flex;
            align-items: center;
            flex-shrink: 0;
            background: #5D4037;
            border-radius: 2px;
            padding: 2px;
          }
 
          /* ── Stats ─── */
          .stats { text-align: right; flex-shrink: 0; }
 
          .total {
            font-size: 15px;
            font-weight: 500;
            color: #1a1a1a;
            font-variant-numeric: tabular-nums;
          }
 
          .winner .total { color: #633806; }
 
          .breakdown {
            font-size: 11px;
            color: #aaa;
            margin-top: 1px;
            white-space: nowrap;
          }
 
          .winner .breakdown { color: #854F0B; }
 
          /* ── Bar chart ─── */
          .bar-section {
            padding: 12px 16px 14px;
            display: flex;
            flex-direction: column;
            gap: 6px;
            border-top: 0.5px solid #e0dfd7;
          }
 
          .bar-row {
            display: grid;
            grid-template-columns: 72px 1fr 24px;
            gap: 0 8px;
            align-items: center;
          }
 
          .bar-name {
            font-size: 11px;
            color: #888;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
 
          .bar-track {
            height: 6px;
            background: #f5f5f3;
            border-radius: 99px;
            overflow: hidden;
          }
 
          .bar-fill {
            height: 100%;
            border-radius: 99px;
            background: #854F0B;
          }
 
          .bar-val {
            font-size: 11px;
            font-weight: 500;
            color: #888;
            text-align: right;
          }
 
          /* ── Footer ─── */
          .footer {
            padding: 10px 16px;
            border-top: 0.5px solid #e0dfd7;
            display: flex;
            align-items: center;
            justify-content: space-between;
            flex-wrap: wrap;
            gap: 8px;
          }
 
          .footer-stat { font-size: 12px; color: #aaa; }
          .footer-stat strong { color: #555; font-weight: 500; }
 
          .empty {
            padding: 2rem 1rem;
            text-align: center;
            font-size: 13px;
            color: #aaa;
          }
 
          @media (max-width: 400px) {
            .row { padding: 10px 12px; gap: 0 10px; }
            /*.card { border-radius: 0; border-left: none; border-right: none; }*/
            .card { border-left: none; border-right: none; }
          }
 
          /*
          @media (prefers-color-scheme: dark) {
            .card        { background: #1e1e1c; border-color: #333330; }
            .title       { color: #f0ede8; }
            .header      { border-bottom-color: #333330; }
            .row         { border-bottom-color: #333330; }
            .row:hover   { background: #2a2a28; }
            .row.winner  { background: #412402; }
            .row.winner:hover { background: #633806; }
            .name        { color: #f0ede8; }
            .winner .name { color: #FAC775; }
            .winner .alias { color: #EF9F27; }
            .winner .total { color: #FAC775; }
            .winner .breakdown { color: #EF9F27; }
            .winner .avatar-placeholder { background: #633806; color: #FAC775; }
            .total       { color: #f0ede8; }
            .avatar-placeholder { background: #1e3a4f; color: #7ec8f7; }
            .bar-section { border-top-color: #333330; }
            .bar-track   { background: #2a2a28; }
            .footer      { border-top-color: #333330; }
            .footer-stat strong { color: #ccc; }
          }
          */
        </style>
 
        <div class="card">
        <div class="header">
          <div class="title">
            <div class="title-icon">${this._cupSVG(16)}</div>
            ${t("daily-ranking")}
          </div>
          <span class="date-label">${this._fmtDate(this._date)}</span>
        </div>
 
          <div class="list" id="list" role="table" aria-label="Coffee ranking">${rowsHTML}</div>
 
          ${barsHTML}
 
          <div class="footer">
            <span class="footer-stat"><strong>${totalCups}</strong> ${t("coffees-today")}</span>
            <span class="footer-stat"><strong>${activeCount}</strong> of ${ranked.length} ${t("active")}</span>
            ${footerChamp}
          </div>
        </div>
      `;
 
      this._bindRowEvents();
    }
  }
 
export { CoffeeRanking }